import React from 'react';
import { Link } from 'gatsby';
import { Heading3, Heading4 } from '@entur/typography';
import './Menu.scss';
import { MenuGroups, MenuLink } from './MenuLinks';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { FormattedMessage } from 'react-intl';
import { FocusTrap } from 'focus-trap-react';

type MenuProps = {
  open?: boolean;
  closeMenu: () => void;
  isDoneAnimate: () => void;
};

const Menu = React.forwardRef<HTMLDivElement, MenuProps>(
  ({ open, closeMenu, isDoneAnimate }, ref) => {
    return (
      <div className="omtur-menu-wrapper">
        <AnimatePresence onExitComplete={isDoneAnimate}>
          {open && (
            <>
              <DropdownMenu ref={ref} key={1} closeMenu={closeMenu} />
              <Overlay key={2} onClick={closeMenu} />
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }
);
const OverlayVariants = {
  closed: { opacity: 0 },
  open: { opacity: 0.5, transition: { duration: 0.3 } },
  out: { opacity: 0, transition: { duration: 0.3 } },
};

type OverlayProps = {
  [key: string]: any;
};

const Overlay = React.forwardRef<HTMLDivElement, OverlayProps>(
  ({ onClick, ...rest }, ref) => {
    return (
      <motion.div
        initial="closed"
        animate="open"
        exit="out"
        variants={OverlayVariants}
        ref={ref}
        {...rest}
        style={{
          height: '88rem',
          background: 'black',
          position: 'absolute',
          width: '100%',
          zIndex: 1,
          cursor: 'pointer',
        }}
        onClick={onClick}
      />
    );
  }
);

const variants: Variants = {
  closed: {
    opacity: 0.7,
    y: 0,
    height: '0rem',
  },
  open: {
    opacity: 1,
    y: 0,
    height: 'auto',
    transition: { delayChildren: 0.15, ease: 'easeOut', duration: 0.15 },
  },
  out: {
    opacity: 1,
    height: '0%',
    transition: {
      delayChildren: 0,
      delay: 0.1,
      ease: 'easeIn',
      duration: 0.15,
    },
  },
};
const item = {
  open: { opacity: 1, y: 0 },
  closed: { opacity: 0, y: 30 },
  out: { opacity: 0, y: 30 },
};

const DropdownMenu = React.forwardRef<
  HTMLDivElement,
  { closeMenu: () => void }
>(({ closeMenu }, ref) => {
  return (
    <>
      <FocusTrap
        focusTrapOptions={{
          onActivate: () => {
            document.body.style.overflow = 'hidden';
          },
          onDeactivate: () => {
            document.body.style.overflow = '';
            closeMenu();
          },
          allowOutsideClick: true,
        }}
      >
        <motion.div
          initial="closed"
          animate="open"
          exit="out"
          variants={variants}
          className="omtur-dropdown-menu__container eds-contrast"
          ref={ref}
        >
          {MenuGroups.map((group, index) => (
            <motion.div
              className="omtur-dropdown-menu__item omtur-dropdown-menu__item--vertical"
              variants={item}
              transition={{ ease: 'easeOut', duration: 0.15 }}
              key={index}
            >
              <Heading3 className="omtur-dropdown-menu__header">
                <FormattedMessage id={group.header} />
              </Heading3>
              <div className="omtur-dropdown-meny__header-underline" />
              <div className="omtur-dropdown-menu__link-collection">
                {group.links.map((link, index) => (
                  <NavLink
                    key={link.name + index}
                    link={link}
                    external={link.url ? link.url.startsWith('https') : false}
                    closeMenu={closeMenu}
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </FocusTrap>
    </>
  );
});

interface NavLinkOptions {
  link: MenuLink;
  external?: boolean;
  closeMenu: MenuProps['closeMenu'];
}

const NavLink = ({ link, external, closeMenu }: NavLinkOptions) => {
  return external ? (
    <a
      className="omtur-dropdown-menu__link"
      href={link.url}
      onClick={closeMenu}
    >
      <Heading4 className="omtur-dropdown-menu__link__text">
        <FormattedMessage id={link.name} />
      </Heading4>
    </a>
  ) : (
    <Link
      to={link.url}
      className="omtur-dropdown-menu__link"
      onClick={closeMenu}
    >
      <Heading4 className="omtur-dropdown-menu__link__text">
        <FormattedMessage id={link.name} />
      </Heading4>
    </Link>
  );
};
export default Menu;
